import { Injectable } from "@angular/core";
import { App } from "@capacitor/app";
import { Browser } from "@capacitor/browser";
import { Capacitor } from "@capacitor/core";
import { ModalController } from "@ionic/angular";
import { lastValueFrom } from "rxjs";
import { environment } from "src/environments/environment";
import { ForceUpdateModalComponent } from "src/app/shared/force-update-modal/force-update-modal.component";
import { AppSettingService, AppVersionModel } from "./swagger";

@Injectable({ providedIn: "root" })
export class MinAppVersionService {
  private modalShown = false;
  private checking = false;

  constructor(
    private appSettingService: AppSettingService,
    private modalController: ModalController,
  ) {}

  async check(): Promise<void> {
    if (Capacitor.getPlatform() === "web") return;
    if (this.modalShown || this.checking) return;
    this.checking = true;
    try {
      const minVersion = await lastValueFrom(
        this.appSettingService.apiAppsettingMinAppVersionGet(),
      );
      if (!minVersion || this.isInvalidVersion(minVersion)) return;

      const installed = this.parseInstalledVersion(environment.hub_version);
      if (!installed) return;

      if (this.isLessThan(installed, minVersion)) {
        await this.showForceUpdateModal();
      }
    } catch {
      // ignore — don't block the user if the check itself fails
    } finally {
      this.checking = false;
    }
  }

  private isInvalidVersion(v: AppVersionModel): boolean {
    return (
      (v.major ?? 0) === 0 &&
      (v.minor ?? 0) === 0 &&
      (v.build ?? 0) === 0 &&
      (v.revision ?? 0) === 0
    );
  }

  private parseInstalledVersion(version: string): AppVersionModel | null {
    if (!version) return null;
    const parts = version.split(".").map((p) => parseInt(p, 10));
    if (parts.some((p) => isNaN(p))) return null;
    return {
      major: parts[0] ?? 0,
      minor: parts[1] ?? 0,
      build: parts[2] ?? 0,
      revision: parts[3] ?? 0,
    };
  }

  private isLessThan(a: AppVersionModel, b: AppVersionModel): boolean {
    const fields: (keyof AppVersionModel)[] = [
      "major",
      "minor",
      "build",
      "revision",
    ];
    for (const f of fields) {
      const av = a[f] ?? 0;
      const bv = b[f] ?? 0;
      if (av < bv) return true;
      if (av > bv) return false;
    }
    return false;
  }

  private async showForceUpdateModal(): Promise<void> {
    this.modalShown = true;
    const top = await this.modalController.getTop();
    if (top) await this.modalController.dismiss();
    const modal = await this.modalController.create({
      component: ForceUpdateModalComponent,
      cssClass: "force-update-modal",
      backdropDismiss: false,
      showBackdrop: true,
    });
    await modal.present();
  }

  async openStore(): Promise<void> {
    const info = await App.getInfo().catch(() => null);
    const pkg = info?.id || "com.coranchers.hub";
    const url = `https://play.google.com/store/apps/details?id=${pkg}`;
    await Browser.open({ url });
  }
}
